import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import EmployeeResourceFilter from 'HomePage/EmployeeResourceFilter';
import Avatar from './Avatar';
import LayoffModal from './LayoffModal';
import PreviewModal from './PreviewModal';
import SignerSelect from './SignerSelect';
import { useCandidates, useSendLayoffs, useSigners } from './useLayoff';
import styles from './layoff.module.scss';

const TABS = { CREW: 'crew', SEARCH: 'search' };

const signerText = (employee, fallbackName) =>
  employee.needs_signer
    ? fallbackName || 'not chosen yet'
    : employee.superintendents.join(' or ');

const EmployeeRow = ({ employee, checked, onToggle, showCrew }) => {
  const id = `layoff-employee-${employee.hash}`;
  const disabled = employee.has_open_layoff;
  return (
    <li
      className={`${styles.employee_row} ${checked ? styles.row_checked : ''} ${disabled ? styles.row_disabled : ''}`}
    >
      <input
        id={id}
        type='checkbox'
        checked={checked}
        disabled={disabled}
        onChange={() => onToggle(employee)}
      />
      <label htmlFor={id} className={styles.employee_label}>
        <Avatar photo={employee.profile_photo} name={employee.name} />
        <span className={styles.employee_text}>
          <span className={styles.employee_name}>{employee.name}</span>
          <span className={styles.muted}>
            {employee.role || 'No role'}
            {showCrew ? ` · ${employee.crew?.name ?? 'No crew'}` : ''}
          </span>
        </span>
        {disabled && <span className={styles.tag}>Open lay-off</span>}
        {!disabled && showCrew && employee.needs_signer && (
          <span className={`${styles.tag} ${styles.tag_warn}`}>
            Pick signer
          </span>
        )}
      </label>
    </li>
  );
};

const SendLayoff = ({ history }) => {
  const [tab, setTab] = useState(TABS.CREW);
  const [crewId, setCrewId] = useState(null);
  const [filter, setFilter] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  // Selected employees keyed by hash, kept across crews and tabs
  const [selected, setSelected] = useState({});
  const [fallbackSigner, setFallbackSigner] = useState('');
  const [tried, setTried] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendErrors, setSendErrors] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setKeyword(keywordInput.trim()), 600);
    return () => clearTimeout(timer);
  }, [keywordInput]);

  const crewQuery = useCandidates(
    { crew_id: crewId },
    tab === TABS.CREW && !!crewId,
  );
  const searchQuery = useCandidates(
    { keyword },
    tab === TABS.SEARCH && keyword.length >= 2,
  );
  const { data: signers = [] } = useSigners();

  // The query may keep the previous crew's data after the crew is cleared
  const crew = crewId ? crewQuery.data?.crew : null;
  const crewMembers = useMemo(() => {
    const members = crewId ? (crewQuery.data?.employees ?? []) : [];
    const term = filter.trim().toLowerCase();
    return term
      ? members.filter((member) => member.name.toLowerCase().includes(term))
      : members;
  }, [crewId, crewQuery.data, filter]);
  const selectableMembers = crewMembers.filter(
    (member) => !member.has_open_layoff,
  );
  const allMembersChecked =
    selectableMembers.length > 0 &&
    selectableMembers.every((member) => selected[member.hash]);

  const selectedList = Object.values(selected);
  const count = selectedList.length;
  const needingSigner = selectedList.filter(
    (employee) => employee.needs_signer,
  );
  const fallbackSignerName = signers.find(
    (signer) => signer.hash === fallbackSigner,
  )?.name;

  const signerSummary = useMemo(() => {
    const groups = new Set(
      selectedList
        .filter((employee) => !employee.needs_signer)
        .map((employee) => employee.superintendents.join(' or ')),
    );
    if (needingSigner.length && fallbackSignerName) {
      groups.add(fallbackSignerName);
    }
    return Array.from(groups);
  }, [selectedList, needingSigner.length, fallbackSignerName]);

  const blocker =
    count > 0 && needingSigner.length > 0 && !fallbackSigner
      ? 'Choose a signer for employees whose crew has no active Superintendent.'
      : '';

  const toggle = (employee) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[employee.hash]) {
        delete next[employee.hash];
      } else {
        next[employee.hash] = employee;
      }
      return next;
    });

  const toggleAllMembers = () =>
    setSelected((prev) => {
      const next = { ...prev };
      selectableMembers.forEach((member) => {
        if (allMembersChecked) {
          delete next[member.hash];
        } else {
          next[member.hash] = member;
        }
      });
      return next;
    });

  const sendLayoffs = useSendLayoffs({
    onSuccess: (response) => {
      const errors = response?.data?.errors ?? [];
      setConfirmOpen(false);
      if (errors.length) {
        // Keep only the employees that failed so they can be fixed and resent
        const failed = new Set(errors.map((error) => error.hash));
        setSelected((prev) =>
          Object.fromEntries(
            Object.entries(prev).filter(([hash]) => failed.has(hash)),
          ),
        );
        setSendErrors(errors);
      } else {
        history.push('/layoff');
      }
    },
    onError: (error) => {
      setConfirmOpen(false);
      setSendErrors(error?.response?.data?.data?.errors ?? []);
    },
  });

  const openConfirm = () => {
    setTried(true);
    if (count > 0 && !blocker) {
      setSendErrors([]);
      setConfirmOpen(true);
    }
  };

  const send = () =>
    sendLayoffs.mutate(
      selectedList.map((employee) => ({
        hash: employee.hash,
        ...(employee.needs_signer ? { signer_hash: fallbackSigner } : {}),
      })),
    );

  const employeeWord = count === 1 ? 'employee' : 'employees';

  return (
    <div className='activity-log'>
      <div className='header'>
        <div className='report_tbl_head sold_rpt_mbl_flx'>
          <h4 className='graph_header'>Send lay-off document</h4>
          <div className='buttons'>
            <Link to='/layoff' className='button outlined'>
              Back to lay-off documents
            </Link>
          </div>
        </div>
      </div>
      <div className='white'>
        <div className={`inner_container col-lg-12 ${styles.send_layout}`}>
          <section aria-label='Choose employees' className={styles.picker}>
            <div
              role='tablist'
              aria-label='How to pick employees'
              className={styles.tabs}
            >
              <button
                type='button'
                role='tab'
                aria-selected={tab === TABS.CREW}
                className={`${styles.tab} ${tab === TABS.CREW ? styles.tab_active : ''}`}
                onClick={() => setTab(TABS.CREW)}
              >
                By crew
              </button>
              <button
                type='button'
                role='tab'
                aria-selected={tab === TABS.SEARCH}
                className={`${styles.tab} ${tab === TABS.SEARCH ? styles.tab_active : ''}`}
                onClick={() => setTab(TABS.SEARCH)}
              >
                Search employees
              </button>
            </div>

            {tab === TABS.CREW && (
              <div role='tabpanel'>
                <div className={styles.filters}>
                  <div className={styles.filter_field}>
                    <span className={styles.field_label}>Crew</span>
                    <EmployeeResourceFilter
                      handleResourceTypeChange={(id) => {
                        setCrewId(id || null);
                        setFilter('');
                      }}
                    />
                  </div>
                  <div className={styles.filter_field}>
                    <label
                      htmlFor='member-filter'
                      className={styles.field_label}
                    >
                      Filter members
                    </label>
                    <input
                      id='member-filter'
                      type='search'
                      className={styles.input}
                      value={filter}
                      onChange={(event) => setFilter(event.target.value)}
                      placeholder='Name'
                      disabled={!crewId}
                    />
                  </div>
                </div>

                {!crewId && (
                  <div className={styles.empty}>
                    Choose a crew to see its members.
                  </div>
                )}

                {crewId && crewQuery.isLoading && (
                  <div className={styles.empty}>Loading crew members...</div>
                )}

                {crew && (
                  <>
                    {crew.superintendents.length > 0 ? (
                      <div className={styles.info_banner}>
                        Superintendents:{' '}
                        <strong>
                          {crew.superintendents.map((s) => s.name).join(', ')}
                        </strong>
                        . Any of them can sign and set the lay-off effective
                        date; the first to sign completes that step.
                      </div>
                    ) : (
                      <div className={styles.warn_banner}>
                        <strong>This crew has no active Superintendent.</strong>{' '}
                        You can still send; choose who signs in the panel on the
                        right.
                      </div>
                    )}

                    <div className={styles.list_box}>
                      <div className={styles.list_header}>
                        <input
                          id='select-all-members'
                          type='checkbox'
                          checked={allMembersChecked}
                          disabled={selectableMembers.length === 0}
                          onChange={toggleAllMembers}
                        />
                        <label htmlFor='select-all-members'>
                          Select all ({crewMembers.length} members)
                        </label>
                        <span className={styles.muted}>
                          {crewMembers.filter((m) => selected[m.hash]).length}{' '}
                          selected
                        </span>
                      </div>
                      <ul className={styles.employee_list}>
                        {crewMembers.map((member) => (
                          <EmployeeRow
                            key={member.hash}
                            employee={member}
                            checked={!!selected[member.hash]}
                            onToggle={toggle}
                          />
                        ))}
                      </ul>
                      {crewMembers.length === 0 && (
                        <div className={styles.empty}>
                          {filter
                            ? `No members match "${filter}".`
                            : 'This crew has no active members.'}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === TABS.SEARCH && (
              <div role='tabpanel'>
                <div className={styles.filter_field}>
                  <label
                    htmlFor='employee-search'
                    className={styles.field_label}
                  >
                    Search all employees
                  </label>
                  <input
                    id='employee-search'
                    type='search'
                    className={styles.input}
                    value={keywordInput}
                    onChange={(event) => setKeywordInput(event.target.value)}
                    placeholder='Name, email or phone'
                  />
                </div>
                {keyword.length < 2 ? (
                  <div className={styles.empty}>
                    Type at least 2 characters to find people in any crew,
                    including office staff with no crew.
                  </div>
                ) : (
                  <div className={styles.list_box}>
                    {searchQuery.isLoading && (
                      <div className={styles.empty}>Searching...</div>
                    )}
                    <ul className={styles.employee_list}>
                      {(searchQuery.data?.employees ?? []).map((employee) => (
                        <EmployeeRow
                          key={employee.hash}
                          employee={employee}
                          checked={!!selected[employee.hash]}
                          onToggle={toggle}
                          showCrew
                        />
                      ))}
                    </ul>
                    {searchQuery.isSuccess &&
                      searchQuery.data.employees.length === 0 && (
                        <div className={styles.empty}>
                          No employees match "{keyword}".
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}
          </section>

          <aside aria-label='Recipients summary' className={styles.summary}>
            <h2 className={styles.summary_title}>Recipients ({count})</h2>

            {sendErrors.length > 0 && (
              <div className={styles.error_banner} role='alert'>
                <strong>Not sent to:</strong>
                <ul>
                  {sendErrors.map((error) => (
                    <li key={error.hash}>
                      {error.name || 'Unknown employee'}: {error.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {count === 0 ? (
              <div className={styles.muted}>
                No one selected yet. Tick employees in the list.
              </div>
            ) : (
              <ul className={styles.chips}>
                {selectedList.map((employee) => (
                  <li key={employee.hash} className={styles.chip}>
                    {employee.name}
                    <button
                      type='button'
                      aria-label={`Remove ${employee.name}`}
                      onClick={() => toggle(employee)}
                    >
                      <span aria-hidden='true'>×</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {needingSigner.length > 0 && (
              <div className={styles.signer_box}>
                <label htmlFor='fallback-signer' className={styles.field_label}>
                  Signer for{' '}
                  {needingSigner.length === 1
                    ? '1 person'
                    : `${needingSigner.length} people`}{' '}
                  without a crew Superintendent
                </label>
                <SignerSelect
                  id='fallback-signer'
                  value={fallbackSigner}
                  onChange={setFallbackSigner}
                  invalid={tried && !fallbackSigner}
                  excludeHashes={needingSigner.map((employee) => employee.hash)}
                />
                <span className={styles.muted}>
                  {needingSigner
                    .map(
                      (employee) =>
                        `${employee.name} (${employee.crew?.name ?? 'No crew'})`,
                    )
                    .join(', ')}
                </span>
              </div>
            )}

            <div className={styles.summary_block}>
              <span className={styles.field_label}>Document</span>
              <span>
                Employee Discharge Form (Seasonal Lay-off), filled in with each
                employee's details
              </span>
            </div>

            <dl className={styles.signers}>
              <dt>Signer 1</dt>
              <dd>
                {signerSummary.length
                  ? signerSummary.join('; ')
                  : 'A Superintendent of each crew'}
              </dd>
              <dt>Signer 2</dt>
              <dd>Each employee</dd>
            </dl>

            <div className={styles.note_box}>
              Once a superintendent signs, these employees will only see this
              document in Firestorm until they sign it. After that, the form is
              reviewed and emailed to Payroll and HR.
            </div>

            {tried && blocker && (
              <div className={styles.error_text} role='alert'>
                {blocker}
              </div>
            )}

            <div className={styles.summary_actions}>
              <button
                type='button'
                className='button outlined'
                onClick={() => setPreviewOpen(true)}
              >
                Preview document
              </button>
              <button
                type='button'
                className='button'
                onClick={openConfirm}
                disabled={count === 0}
              >
                Send to {count} {employeeWord}
              </button>
            </div>
          </aside>
        </div>
      </div>

      <LayoffModal
        isOpen={confirmOpen}
        title={`Send lay-off document to ${count} ${employeeWord}?`}
        onClose={() => setConfirmOpen(false)}
      >
        <ul className={styles.confirm_list}>
          {selectedList.map((employee) => (
            <li key={employee.hash}>
              <strong>{employee.name}</strong>
              <span className={styles.muted}>
                {employee.crew?.name ?? 'No crew'} · signs:{' '}
                {signerText(employee, fallbackSignerName)}
              </span>
            </li>
          ))}
        </ul>
        <p>
          Signers and employees get an in-app notification and an email.
          Employees keep normal access until a superintendent signs, then are
          locked to this document until they sign.
        </p>
        <div className={styles.modal_actions}>
          <button
            type='button'
            className='button grey'
            onClick={() => setConfirmOpen(false)}
          >
            Cancel
          </button>
          <button
            type='button'
            className='button'
            onClick={send}
            disabled={sendLayoffs.isLoading}
          >
            {sendLayoffs.isLoading ? 'Sending...' : 'Send now'}
          </button>
        </div>
      </LayoffModal>

      <PreviewModal
        isOpen={previewOpen}
        employee={selectedList[0]}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
};

export default SendLayoff;
